import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Container,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { useAuth } from '../../hooks/useAuth';
import { useOnboardingFlow } from '../../hooks/onboarding/useOnboardingFlow';
import { useKeyboardNavigation } from '../../hooks/onboarding/useKeyboardNavigation';
import StepNavigation from './common/StepNavigation';
import { useAnalyticsContext } from '../../contexts/AnalyticsContext';

// Import all step components
import WelcomeStep from './steps/WelcomeStep';
import BasicInfoStep from './steps/BasicInfoStep';
import AvatarStep from './steps/AvatarStep';
import AcademicStep from './steps/AcademicStep';
import CoursesStep from './steps/CoursesStep';
import CompletionStep from './steps/CompletionStep';

const OnboardingContainer: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { currentStep } = useOnboardingStore();
  const { goNext, goPrevious, canProceed, isFirstStep, isLastStep } = useOnboardingFlow();
  const [previousStep, setPreviousStep] = useState(currentStep);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const { trackEvent } = useAnalyticsContext();
  const stepStartTime = useRef<number>(Date.now());
  const onboardingStartTime = useRef<number>(Date.now());
  
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50)',
    'linear(to-br, gray.900, purple.900)'
  );
  const containerBg = 'transparent';
  const borderColor = 'transparent';

  // Step names for analytics
  const getStepName = (step: number): string => {
    const stepNames = {
      1: 'Welcome',
      2: 'Basic Info',
      3: 'Avatar Selection',
      4: 'Academic Info',
      5: 'Course Preferences',
      6: 'Completion'
    };
    return stepNames[step] || 'Unknown';
  };

  // Track onboarding start
  useEffect(() => {
    trackEvent('onboarding_started', {
      user_id: user?.id,
      timestamp: new Date().toISOString()
    });
    onboardingStartTime.current = Date.now();

    // Track abandonment on unmount (if not completed)
    return () => {
      if (currentStep !== 6) {
        const timeSpent = Date.now() - onboardingStartTime.current;
        trackEvent('onboarding_abandoned', {
          last_step: currentStep,
          last_step_name: getStepName(currentStep),
          time_spent_ms: timeSpent,
          time_spent_seconds: Math.round(timeSpent / 1000),
          user_id: user?.id,
          timestamp: new Date().toISOString()
        });
      }
    };
  }, []);

  // Check if user should be in onboarding
  useEffect(() => {
    if (user && user.profile_exists === true) {
      toast({
        title: 'Profile already complete',
        description: 'Redirecting to dashboard...',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      navigate('/dashboard');
    }
  }, [user, navigate, toast]);

  // Track step changes and timing
  useEffect(() => {
    if (previousStep !== currentStep && previousStep !== 0) {
      const timeOnStep = Date.now() - stepStartTime.current;
      
      // Track step transition
      trackEvent('onboarding_step_transition', {
        from_step: previousStep,
        from_step_name: getStepName(previousStep),
        to_step: currentStep,
        to_step_name: getStepName(currentStep),
        time_on_previous_step_ms: timeOnStep,
        time_on_previous_step_seconds: Math.round(timeOnStep / 1000),
        user_id: user?.id,
        direction: currentStep > previousStep ? 'forward' : 'backward'
      });

      // Track step view
      trackEvent('onboarding_step_viewed', {
        step_number: currentStep,
        step_name: getStepName(currentStep),
        user_id: user?.id,
        time_since_start_ms: Date.now() - onboardingStartTime.current,
        timestamp: new Date().toISOString()
      });

      // Reset step timer
      stepStartTime.current = Date.now();
    }

    // Handle animation direction
    if (currentStep > previousStep) {
      setSlideDirection('left'); // Next: new content comes from right, moves left
    } else if (currentStep < previousStep) {
      setSlideDirection('right'); // Back: new content comes from left, moves right
    }
    setPreviousStep(currentStep);
  }, [currentStep, previousStep, trackEvent, user]);

  // Render the current step based on state
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep />;
      case 2:
        return <BasicInfoStep />;
      case 3:
        return <AvatarStep />;
      case 4:
        return <AcademicStep />;
      case 5:
        return <CoursesStep />;
      case 6:
        return <CompletionStep />;
      default:
        return <WelcomeStep />;
    }
  };

  // Handle navigation for completion step
  const handleNext = () => {
    if (currentStep === 6) {
      // Track onboarding completion
      const totalTime = Date.now() - onboardingStartTime.current;
      trackEvent('onboarding_completed', {
        total_time_ms: totalTime,
        total_time_seconds: Math.round(totalTime / 1000),
        total_time_minutes: Math.round(totalTime / 60000),
        user_id: user?.id,
        timestamp: new Date().toISOString()
      });
      
      navigate('/dashboard');
    } else {
      // Track step completion
      trackEvent('onboarding_step_completed', {
        step_number: currentStep,
        step_name: getStepName(currentStep),
        time_on_step_ms: Date.now() - stepStartTime.current,
        user_id: user?.id
      });
      
      goNext();
    }
  };

  // Handle going back
  const handlePrevious = () => {
    // Track going back
    trackEvent('onboarding_step_back', {
      from_step: currentStep,
      from_step_name: getStepName(currentStep),
      to_step: currentStep - 1,
      to_step_name: getStepName(currentStep - 1),
      user_id: user?.id
    });
    
    goPrevious();
  };

  // Add keyboard navigation
  useKeyboardNavigation({
    onNext: handleNext,
    isEnabled: true,
  });

  return (
    <Box
      h="100vh"
      bgGradient={bgGradient}
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Container maxW="container.xl" flex="1" display="flex" flexDirection="column" justifyContent="center" py={8}>
        <Box
          bg={containerBg}
          height="auto"
          minHeight="500px"
          display="flex"
          flexDirection="column"
          position="relative"
          overflow="hidden"
        >
          <Box
            flex="1"
            overflowY="auto"
            px={{ base: 6, md: 12 }}
            pt={{ base: 4, md: 8 }}
            pb={{ base: 6, md: 12 }}
            sx={{
              overscrollBehavior: 'contain',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '::-webkit-scrollbar': {
                width: '0px',
                height: '0px',
              },
            }}
          >
            <Box
              key={currentStep}
              animation={`${slideDirection === 'left' ? 'slideFromRight' : 'slideFromLeft'} 0.4s ease-out`}
              sx={{
                willChange: 'transform, opacity',
                '@keyframes slideFromRight': {
                  from: {
                    transform: 'translateX(50px)',
                    opacity: 0,
                  },
                  to: {
                    transform: 'translateX(0)',
                    opacity: 1,
                  },
                },
                '@keyframes slideFromLeft': {
                  from: {
                    transform: 'translateX(-50px)',
                    opacity: 0,
                  },
                  to: {
                    transform: 'translateX(0)',
                    opacity: 1,
                  },
                },
              }}
              overflow="hidden"
            >
              {renderStep()}
            </Box>
          </Box>
        </Box>
      </Container>
      
      {/* Navigation buttons - outside the container (hidden on welcome page) */}
      {currentStep !== 1 && (
        <StepNavigation
          onNext={handleNext}
          onPrevious={handlePrevious}
          isFirstStep={isFirstStep && currentStep !== 6}
          isLastStep={currentStep === 6}
          isNextDisabled={!canProceed() && currentStep !== 6}
        />
      )}
    </Box>
  );
};

export default OnboardingContainer;